/**
 * @module page-detail/pageDetailUi
 * @see apps/web/src/ui/pages/PageDetail.tsx
 */

import React from 'react';
import { Button, Card, CardContent, Flex, Heading, SplitButton, StackedBarChart, Text, TagBadge, type BadgeVariant, badgeVariantToTagColor } from '@chatsdet/ui';
import { getQaStatusMeta } from '../qaStatus';
import type { QaReportSummary, TaxonomyElementType } from './types';
import { TAXONOMY_LABELS } from './types';

export function formatDate(value: string | null | undefined) {
  if (!value) return 'Unknown';
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export function formatRelativeTime(value?: string | null) {
  if (!value) return 'Unknown';
  try {
    const date = new Date(value);
    const diffMs = Date.now() - date.getTime();
    const minutes = Math.round(diffMs / 60000);
    if (Math.abs(minutes) < 60) {
      return `${minutes}m`;
    }
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) {
      return `${hours}h`;
    }
    const days = Math.round(hours / 24);
    return `${days}d`;
  } catch {
    return formatDate(value);
  }
}

export function formatRelativeTimeAgo(value?: string | null) {
  if (!value) return 'Unknown';
  try {
    const date = new Date(value);
    const diffMs = Date.now() - date.getTime();
    const minutes = Math.round(diffMs / 60000);
    if (Math.abs(minutes) < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 24) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  } catch {
    return formatDate(value);
  }
}

function formatChartDate(date: Date) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(date);
  } catch {
    return '';
  }
}

function InfoStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="sm" className="text-muted-foreground">
        {label}
      </Text>
      <Text weight="medium">{value}</Text>
    </div>
  );
}

type QaBarChartDataItem = {
  id: string | null;
  index: number;
  date: string;
  dateDisplay: string;
  hasRun: boolean;
  runCount: number;
  passed: number;
  failed: number;
  notApplicable: number;
  pending: number;
};

export function buildQaBarChartData(timelineReports: QaReportSummary[]): QaBarChartDataItem[] {
  const days = 30;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const reportsByDate = new Map<string, QaReportSummary[]>();
  for (const report of timelineReports) {
    const dateStr = report.completedAt ?? report.startedAt;
    if (!dateStr) {
      continue;
    }

    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    const key = date.toISOString().split('T')[0];
    const existing = reportsByDate.get(key) ?? [];
    existing.push(report);
    reportsByDate.set(key, existing);
  }

  const data: QaBarChartDataItem[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const key = date.toISOString().split('T')[0];
    const reports = reportsByDate.get(key) ?? [];

    const counts = { passed: 0, failed: 0, notApplicable: 0, pending: 0 };
    let reportId: string | null = null;
    for (const report of reports) {
      if (!reportId) {
        reportId = report.id;
      }
      for (const todo of report.todoItems ?? []) {
        if (todo.status === 'healthy' || todo.status === 'done') counts.passed++;
        else if (todo.status === 'unhealthy') counts.failed++;
        else if (todo.status === 'not_applicable') counts.notApplicable++;
        else counts.pending++;
      }
    }

    data.push({
      id: reportId,
      index: data.length,
      date: key,
      dateDisplay: formatChartDate(date),
      hasRun: reports.length > 0,
      runCount: reports.length,
      ...counts,
    });
  }

  return data;
}

type QaActivityChartProps = {
  chartData: QaBarChartDataItem[];
  selectedReportId: string | null;
  onSelectReport: (id: string) => void;
  error?: string | null;
};

export function QaActivityChart({
  chartData,
  selectedReportId,
  onSelectReport,
  error,
}: QaActivityChartProps) {
  const selectedBarIndex = React.useMemo(() => {
    if (!selectedReportId) return null;
    const idx = chartData.findIndex((d) => d.id === selectedReportId);
    return idx >= 0 ? idx : null;
  }, [chartData, selectedReportId]);

  const handleBarClick = (data: { id?: string | null; hasRun?: boolean }) => {
    if (data.id && data.hasRun) {
      onSelectReport(data.id);
    }
  };

  const renderTooltip = (data: QaBarChartDataItem) => {
    if (!data.hasRun) {
      return (
        <div className="space-y-1">
          <Text size="sm" weight="medium">
            {data.dateDisplay}
          </Text>
          <Text size="sm" className="text-muted-foreground">
            No QA run
          </Text>
        </div>
      );
    }

    const total = data.passed + data.failed + data.notApplicable + data.pending;
    return (
      <div className="space-y-2">
        <Text size="sm" weight="medium">
          {data.dateDisplay}
        </Text>
        <div className="space-y-1">
          {data.passed > 0 ? (
            <Flex align="center" gap="xs">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'hsl(var(--success))' }} />
              <Text size="sm">Passed: {data.passed}</Text>
            </Flex>
          ) : null}
          {data.failed > 0 ? (
            <Flex align="center" gap="xs">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: 'hsl(var(--destructive))' }}
              />
              <Text size="sm">Failed: {data.failed}</Text>
            </Flex>
          ) : null}
          {data.notApplicable > 0 ? (
            <Flex align="center" gap="xs">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: 'hsl(var(--warning))' }} />
              <Text size="sm">N/A: {data.notApplicable}</Text>
            </Flex>
          ) : null}
          {data.pending > 0 ? (
            <Flex align="center" gap="xs">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: 'hsl(var(--muted-foreground))' }}
              />
              <Text size="sm">Pending: {data.pending}</Text>
            </Flex>
          ) : null}
        </div>
        <Text size="xs" className="text-muted-foreground">
          {total} test{total !== 1 ? 's' : ''} • {data.runCount} run{data.runCount !== 1 ? 's' : ''}
        </Text>
      </div>
    );
  };

  if (error) {
    return (
      <Text size="sm" className="text-destructive">
        {error}
      </Text>
    );
  }

  return (
    <div className="space-y-2">
      <Flex align="center" justify="between">
        <Text weight="medium" className="text-sm text-muted-foreground">
          QA Activity - Last 30 days
        </Text>
      </Flex>
      <StackedBarChart
        data={chartData}
        xDataKey="date"
        stacks={[
          { dataKey: 'passed', name: 'Passed', color: 'hsl(var(--success))' },
          { dataKey: 'failed', name: 'Failed', color: 'hsl(var(--destructive))' },
          { dataKey: 'notApplicable', name: 'N/A', color: 'hsl(var(--warning))' },
          { dataKey: 'pending', name: 'Pending', color: 'hsl(var(--muted-foreground))' },
        ]}
        selectedIndex={selectedBarIndex}
        onBarClick={handleBarClick}
        height={80}
        showLegend={false}
        showYAxis={false}
        barGap="2%"
        margin={{ top: 4, right: 0, left: 0, bottom: 4 }}
        minBarHeight={0.5}
        renderTooltip={renderTooltip as (data: Record<string, unknown>) => React.ReactNode}
      />
    </div>
  );
}

type QaReportCardProps = {
  qaReport: QaReportSummary | null;
  onRunAgent: () => void;
  onRunAgentWithLatestRun: () => void;
  onContinueRun: (reportId: string) => void;
  onViewReport: (report: QaReportSummary | null) => void;
  isRunning: boolean;
  isAttaching: boolean;
  isContinuing: boolean;
};

export function QaReportCard({
  qaReport,
  onRunAgent,
  onRunAgentWithLatestRun,
  onContinueRun,
  onViewReport,
  isRunning,
  isAttaching,
  isContinuing,
}: QaReportCardProps) {
  const statusMeta = getQaStatusMeta(qaReport?.status);
  const canContinue = canContinueQaReport(qaReport);
  const isBusy = isRunning || isAttaching || isContinuing;
  const primaryLabel = isRunning
    ? 'Starting…'
    : isAttaching
      ? 'Adding…'
      : isContinuing
        ? 'Continuing…'
        : canContinue
          ? 'Continue QA run'
          : qaReport
            ? 'Re-run QA'
            : 'Run QA Agent';
  const dropdownLabel = isAttaching ? 'Adding to latest QA run…' : 'Run & add to latest QA run';

  return (
    <Card variant="elevated" padding="lg">
      <CardContent className="space-y-4">
        <Flex align="center" justify="between">
          <Heading size="sm">Latest QA Report</Heading>
          {qaReport ? <TagBadge name={statusMeta.label} color={badgeVariantToTagColor(statusMeta.badgeVariant)} hideDot /> : null}
        </Flex>

        {qaReport ? (
          <>
            <Text size="sm" className="text-muted-foreground">
              {qaReport.summary ?? 'QA run completed without summary.'}
            </Text>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
              <InfoStat
                label="Completed"
                value={formatRelativeTime(qaReport.completedAt ?? qaReport.startedAt)}
              />
              <InfoStat label="Tests covered" value={qaReport.testedItems?.length ?? 0} />
              <InfoStat label="Issues found" value={qaReport.issuesFoundCount ?? 0} />
              <InfoStat label="Status" value={statusMeta.label} />
            </div>
            <Flex gap="sm" wrap justify="end" className="w-full">
              <Button variant="primary" size="md" onPress={() => onViewReport(qaReport)}>
                View report
              </Button>
              <SplitButton
                variant="secondary"
                size="md"
                onPress={
                  canContinue && qaReport?.id ? () => onContinueRun(qaReport.id) : onRunAgent
                }
                isDisabled={isBusy}
              >
                {primaryLabel}
                {canContinue && qaReport?.id ? (
                  <SplitButton.Item onAction={() => onContinueRun(qaReport.id)} isDisabled={isBusy}>
                    {isContinuing ? 'Continuing…' : 'Continue QA run'}
                  </SplitButton.Item>
                ) : null}
                <SplitButton.Item onAction={() => onRunAgentWithLatestRun()} isDisabled={isBusy}>
                  {dropdownLabel}
                </SplitButton.Item>
              </SplitButton>
            </Flex>
          </>
        ) : (
          <div className="space-y-3">
            <Text size="sm" className="text-muted-foreground">
              The QA agent has not run on this page yet. Kick off a run to capture coverage.
            </Text>
            <Flex justify="end">
              <SplitButton variant="primary" size="md" onPress={onRunAgent} isDisabled={isBusy}>
                {primaryLabel}
                <SplitButton.Item onAction={() => onRunAgentWithLatestRun()} isDisabled={isBusy}>
                  {dropdownLabel}
                </SplitButton.Item>
              </SplitButton>
            </Flex>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function renderElementTypeBadge(type: TaxonomyElementType) {
  let variant: BadgeVariant;
  switch (type) {
    case 'data_table':
      variant = 'default';
      break;
    case 'form':
      variant = 'success';
      break;
    case 'table':
    case 'link':
    case 'input':
      variant = 'outline';
      break;
    case 'button':
      variant = 'secondary';
      break;
    case 'other':
    default:
      variant = 'warning';
      break;
  }

  return (
    <TagBadge name={TAXONOMY_LABELS[type]} color={badgeVariantToTagColor(variant)} hideDot size="sm" className="capitalize" />
  );
}

export function canContinueQaReport(report: QaReportSummary | null): boolean {
  if (!report) return false;
  if (report.status === 'running' || report.status === 'pending') return false;
  return (
    report.status === 'failed' || (report.todoItems ?? []).some((todo) => todo.status === 'pending')
  );
}
